import { createContext, useContext, useEffect, useState } from 'react';
import { TFlat } from '../shared/types/flat';

type TFlatsDataContext = {
    contextShowSidebar: boolean;
    contextFlats: TFlat[];
    contextSelectedFlat: TFlat;
    contextSelectedFloor: string;
    contextHoveredFlat: any;
    contextFetchedFlats: boolean;
    setContextFlats: (flats: TFlat[]) => void;
    setContextSelectedFlat: (flat: any) => void;
    setContextSelectedFloor: (floor: string) => void;
    setContextHoveredFlat: (flat: any) => void;
    setFilterAvailable: (filter: any) => void;
    setSecondarySingleFilter: (key: string, value: any) => void
    setContextSetShowSidebar: (value: boolean) => void;
};

const FlatsDataContext = createContext<TFlatsDataContext | undefined>(undefined);

export const FlatsDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
    const [ contextAllFlats, setContextAllFlats ] = useState<TFlat[]>([]);
    const [ contextFlats, setContextFlats ] = useState<TFlat[]>([]);
    const [ contextSelectedFlat, setContextSelectedFlat ] = useState<any>({name: ''});
    const [ contextSelectedFloor, setContextSelectedFloor ] = useState<string>('');
    const [ contextHoveredFlat, setContextHoveredFlat ] = useState<any>({ name: ''});
    const [ contextShowSidebar, setContextSetShowSidebar]  = useState<boolean>(false);
    const [ contextSecondaryFilters, setContextSecondaryFilters ] = useState({ rooms: [] });
    const [ contextFetchedFlats, setContextFetchedFlats ] = useState<boolean>(false);
    const [ filterAvailable, setFilterAvailable ] = useState<string[]>(['all']);

    /**
     * Updates a single secondary filter with the given key and value.
     * @param {string} key - The key for the filter to update ('rooms', 'areaFrom', 'areaTo').
     * @param {any} value - The new value for the specified filter key.
    */
    const setSecondarySingleFilter = (key: string, value: any): void => {
        setContextSecondaryFilters(prevFilters => ({
            ...prevFilters,
            [key]: value,
        }));
    };

    useEffect(() => {
        let ignore = false;
        setContextFlats([]);
  
        const fetchFlats = async () => {
            try {
                const apiUrl = window.location.hostname === 'localhost' 
                    ? 'https://estate.globeweb.stronazen.pl/wp-json'
                    : '/wp-json';
                    
                const response = await fetch(`${apiUrl}/gw-estate-crm/v1/flats`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                
                const rawFlatsData = await response.json();
                console.log('Raw API response:', rawFlatsData);
                
                const mappedFlatsData = rawFlatsData.map((flat: any) => ({
                    id: flat.id,
                    name: flat.title,
                    image: flat.meta?.images?.[0]?.url || '',
                    file: flat.meta?.description || '',
                    file_jpg: flat.meta?.images?.[0]?.url || '',
                    area: parseInt(flat.meta?.area) || 0,
                    details: flat.content || '',
                    rooms: parseInt(flat.meta?.rooms) || 0,
                    status: flat.status?.slug || 'unknown',
                    floor: flat.floor ? {
                        id: flat.floor.id,
                        name: flat.floor.name,
                        slug: flat.floor.slug.replace('-', '_')
                    } : { id: 0, name: '', slug: '' },
                    additionalSpace: flat.meta?.features ? 
                        (typeof flat.meta.features === 'string' 
                            ? flat.meta.features.split(',').map((f: string) => f.trim())
                            : flat.meta.features
                        ) : []
                }));
                
                console.log('Mapped flats data:', mappedFlatsData);
                
                if (!ignore) {
                    setContextFlats([...mappedFlatsData]);
                    setContextAllFlats([...mappedFlatsData]);
                    setContextFetchedFlats(true);
                }
            } catch (error) {
                if (!ignore) {
                    console.error('Error fetching flats:', error);
                    console.error('Szczegóły błędu:', {
                        message: error instanceof Error ? error.message : 'Unknown error',
                        endpoint: `${window.location.hostname === 'localhost' 
                            ? 'https://estate.globeweb.stronazen.pl/wp-json'
                            : '/wp-json'}/gw-estate-crm/v1/flats`
                    });
                }
            }
        };
  
        fetchFlats();
  
        return () => {
            ignore = true;
        };
    }, [setContextFlats]);

    useEffect(() => {
        setContextFlats([
                ...contextAllFlats.filter(flat => {
                    let matchesFloor = true;
                    let matchesRooms = true;
                    let matchesArea = true;

                    if(contextSelectedFloor) {
                        matchesFloor = flat.floor.slug === contextSelectedFloor;
                    }

                    if(contextSecondaryFilters.rooms.length > 0) {
                        //@ts-ignore
                        matchesRooms = contextSecondaryFilters.rooms.includes(flat.rooms);
                    }
                    //@ts-ignore
                    matchesArea = flat.area >= (contextSecondaryFilters.areaFrom || 0) && flat.area <= (contextSecondaryFilters.areaTo || 250);
                    
                    const matchesAvailability = filterAvailable.includes('all') || filterAvailable.includes(flat.status) || filterAvailable.length === 0;

                    return matchesFloor && matchesAvailability && matchesRooms && matchesArea;
                })
            ]);
    }, [filterAvailable, contextSelectedFloor, contextSecondaryFilters]);

    return (
        <FlatsDataContext.Provider value={{ 
            contextShowSidebar,
            contextFlats, 
            contextSelectedFlat, 
            contextSelectedFloor,
            contextHoveredFlat, 
            contextFetchedFlats,
            setContextFlats, 
            setContextSelectedFlat,  
            setContextSelectedFloor, 
            setContextHoveredFlat, 
            setFilterAvailable, 
            setSecondarySingleFilter,
            setContextSetShowSidebar
        }}>
            {children}
        </FlatsDataContext.Provider>
    );
};

export const useFlatsDataContext = (): TFlatsDataContext => {
    const context = useContext(FlatsDataContext);
    if (!context) {
        throw new Error('useFlatsData must be used within a FlatsDataProvider');
    }
    return context;
};